import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  FottServiceRequestStatus,
  type TerminalServiceTask,
} from "@services/terminal-services/types";

export type TerminalServiceFlowData = {
  id?: string;
  status?: string | null;
  task?: TerminalServiceTask | null;
};

export const TERMINAL_SERVICE_TASK_STATUSES = {
  READY: "READY",
  CLAIMED: "CLAIMED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  TERMINATED: "TERMINATED",
} as const;

const isTaskNotClaimed = (taskStatus?: string | null, claimOwnerName?: string | null) =>
  taskStatus === TERMINAL_SERVICE_TASK_STATUSES.READY && !claimOwnerName;

const isTaskClaimed = (taskStatus?: string | null) =>
  taskStatus === TERMINAL_SERVICE_TASK_STATUSES.CLAIMED;

const isTaskOwner = (claimOwnerName?: string | null) => {
  const user = useAuthStore.getState().user;

  if (!claimOwnerName) {
    return false;
  }

  return claimOwnerName === user?.email || claimOwnerName === user?.preferredUsername;
};

const isServiceClaimable = (status?: string | null) =>
  status === FottServiceRequestStatus.PENDING_APPROVAL;

export const canUserClaimTerminalService = (
  taskStatus?: string | null,
  claimOwnerName?: string | null,
  status?: string | null,
) => isTaskNotClaimed(taskStatus, claimOwnerName) && isServiceClaimable(status);

export const canUserUnclaimTerminalService = (
  taskStatus?: string | null,
  claimOwnerName?: string | null,
  status?: string | null,
) => isTaskClaimed(taskStatus) && isTaskOwner(claimOwnerName) && isServiceClaimable(status);

export const canUserApproveTerminalService = (
  taskStatus?: string | null,
  claimOwnerName?: string | null,
  status?: string | null,
) => isTaskClaimed(taskStatus) && isTaskOwner(claimOwnerName) && isServiceClaimable(status);

export const canUserRejectTerminalService = canUserApproveTerminalService;
const isCustomer = () => {
  const user = useAuthStore.getState().user;

  return user?.roles.includes("customer") ?? false;
};

export const canUserUpdateTerminalService = (
  _taskStatus?: string | null,
  _claimOwnerName?: string | null,
  status?: string | null,
) => status === FottServiceRequestStatus.REJECTED && isCustomer();

export const getTerminalServiceFlowActionAvailability = ({
  taskStatus,
  claimOwnerName,
  status,
}: {
  taskStatus?: string | null;
  claimOwnerName?: string | null;
  status?: string | null;
}) => ({
  canClaim: canUserClaimTerminalService(taskStatus, claimOwnerName, status),
  canUnclaim: canUserUnclaimTerminalService(taskStatus, claimOwnerName, status),
  canApprove: canUserApproveTerminalService(taskStatus, claimOwnerName, status),
  canReject: canUserRejectTerminalService(taskStatus, claimOwnerName, status),
  canUpdate: canUserUpdateTerminalService(taskStatus, claimOwnerName, status),
});
